import { runtimeGraphIds, type RuntimeNodeImport } from '@/models/logic/RuntimeGraphIds';
import { makeAutoObservable, runInAction } from 'mobx';
import { Point } from '@/models/Point';
import { Bounds2D } from '@/utils/bounds';
import { Direction } from '@/utils/direction';
import { TileId } from '@/types/ids';
import { TileInstance } from '@/models/tiles/TileInstance';
import { TileManagerDtoSchema } from '@/schemas/circuit';
import type { TileInstanceDto } from '@/schemas/tiles';
import Context from '@/context';
import type { IPoint, ITileManagerModel } from '@/interfaces/models';
import { isInterfaceTile } from './tiles/TileRegistry';
import { ChangeJournal } from '@/utils/ChangeJournal';

export const MIN_TILE_POSITION_VALUE = -16383;
export const MAX_TILE_POSITION_VALUE = 16384;

export const TILE_POSITION_VALUE_RANGE =
  MAX_TILE_POSITION_VALUE - MIN_TILE_POSITION_VALUE;

const CHUNK_SIZE = 16;
const chunkKey = (x: number, y: number) =>
  toIndex(Math.floor(x / CHUNK_SIZE), Math.floor(y / CHUNK_SIZE));
export const isValidTilePosition = (x: number, y: number): boolean =>
  Number.isInteger(x) &&
  Number.isInteger(y) &&
  x >= MIN_TILE_POSITION_VALUE &&
  x <= MAX_TILE_POSITION_VALUE &&
  y >= MIN_TILE_POSITION_VALUE &&
  y <= MAX_TILE_POSITION_VALUE;

export function toIndex(a: number, b: number): number {
  const aMapped = a - MIN_TILE_POSITION_VALUE;
  const bMapped = b - MIN_TILE_POSITION_VALUE;

  return aMapped | (bMapped << 15);
}

export function fromIndex(c: number): [number, number] {
  const b = c >> 15;
  const a = c ^ (b << 15);

  return [a + MIN_TILE_POSITION_VALUE, b + MIN_TILE_POSITION_VALUE];
}

export interface TileManagerDto {
  tiles: TileInstanceDto[];
}

const constructedTileCollections = new WeakMap<object, TileInstance[]>();
const nativeLookups = new WeakMap<object, unknown[]>();
const lookupMethods = [
  'get',
  'getRaw',
  'getById',
  'getNeighbors',
  'has',
  'contains',
] as const;

export function hasNativeTileLookups(manager: ITileManagerModel): boolean {
  const original = nativeLookups.get(manager);
  return (
    !!original && lookupMethods.every((key, i) => (manager as any)[key] === original[i])
  );
}

/** Only the initial native observable collection has known membership semantics. */
export function isConstructedTileCollection(
  manager: ITileManagerModel,
  tiles: TileInstance[],
): boolean {
  const constructed = constructedTileCollections.get(manager);
  if (constructed === tiles) return true;
  // Do not retain a replaced collection (and its removed tiles) for the lifetime
  // of a manager. Custom replacement permanently retires this certification.
  if (constructed && manager.tiles !== constructed) {
    constructedTileCollections.delete(manager);
  }
  return false;
}

export class TileManagerModel implements ITileManagerModel {
  tileMap: Record<number, TileInstance> = {};
  tiles: TileInstance[] = [];
  structureVersion = 0;
  private chunks = new Map<number, Map<number, TileInstance>>();
  private tilesById = new Map<TileId, TileInstance>();
  private changes = new ChangeJournal<TileId>();

  static fromDto(
    tileManagerDto: TileManagerDto,
    context: Context,
    importer: RuntimeNodeImport = runtimeGraphIds(context).importer(),
  ) {
    const steps = this.fromDtoSteps(tileManagerDto, context, importer);
    let result = steps.next();
    while (!result.done) result = steps.next();
    return result.value;
  }

  static *fromDtoSteps(
    tileManagerDto: TileManagerDto,
    context: Context,
    importer: RuntimeNodeImport = runtimeGraphIds(context).importer(),
    onProgress?: import('@/utils/projectLoadProgress').ProjectLoadWorkObserver,
  ): Generator<void, TileManagerModel> {
    // TileInstance.fromDto validates every entry. Avoid parsing the entire array
    // a second time, and yield between batches so large chips can paint.
    if (!Array.isArray(tileManagerDto?.tiles)) TileManagerDtoSchema.parse(tileManagerDto);
    const manager = new TileManagerModel(context);
    for (let i = 0; i < tileManagerDto.tiles.length; i += 128) {
      manager.addAll(
        tileManagerDto.tiles
          .slice(i, i + 128)
          .map((tileDto) => TileInstance.fromDto(tileDto, context, importer)),
      );
      onProgress?.(
        Math.min(i + 128, tileManagerDto.tiles.length),
        tileManagerDto.tiles.length,
      );
      yield;
    }
    return manager;
  }

  static fromTiles(tiles: TileInstance[], context: Context) {
    const manager = new TileManagerModel(context);
    manager.addAll(tiles);
    return manager;
  }

  constructor(private context: Context) {
    makeAutoObservable<this, 'chunks' | 'tilesById' | 'changes'>(
      this,
      { chunks: false, tilesById: false, changes: false, toDtoSteps: false } as any,
      { autoBind: true },
    );
    constructedTileCollections.set(this, this.tiles);
    nativeLookups.set(
      this,
      lookupMethods.map((key) => this[key]),
    );
  }

  *toDtoSteps(): Generator<void, TileManagerDto> {
    const tiles: TileInstanceDto[] = [];
    for (const tile of this.tiles) {
      tiles.push(tile.dto);
      if (tiles.length % 128 === 0) yield;
    }
    return { tiles };
  }

  add(tile: TileInstance) {
    runInAction(() => {
      const { x, y } = tile.location;
      if (!isValidTilePosition(x, y)) throw new Error('Tile position out of bounds');
      const index = toIndex(x, y);
      if (this.tileMap[index] === tile) return;
      if (this.tileMap[index]) throw new Error('Tile position occupied');
      this.tileMap[index] = tile;
      this.tilesById.set(tile.id, tile);
      this.tiles.push(tile);
      const key = chunkKey(x, y);
      let chunk = this.chunks.get(key);
      if (!chunk) this.chunks.set(key, (chunk = new Map()));
      chunk.set(index, tile);
      this.structureVersion++;
      this.changes.record(this.structureVersion, [tile.id]);
    });
  }

  addAll(tiles: TileInstance[]) {
    const pending = new Map<number, TileInstance>();
    for (const tile of tiles) {
      const { x, y } = tile.location;
      if (!isValidTilePosition(x, y)) throw new Error('Tile position out of bounds');
      const index = toIndex(x, y);
      if (
        (this.tileMap[index] && this.tileMap[index] !== tile) ||
        (pending.has(index) && pending.get(index) !== tile)
      )
        throw new Error('Tile position occupied');
      pending.set(index, tile);
    }
    if (tiles.length === 0) return;
    runInAction(() => {
      for (const tile of tiles) {
        const { x, y } = tile.location;
        const index = toIndex(x, y);
        if (this.tileMap[index] === tile) continue;
        this.tileMap[index] = tile;
        this.tilesById.set(tile.id, tile);
        this.tiles.push(tile);
        const key = chunkKey(x, y);
        let chunk = this.chunks.get(key);
        if (!chunk) this.chunks.set(key, (chunk = new Map()));
        chunk.set(index, tile);
      }
      this.structureVersion++;
      this.changes.record(
        this.structureVersion,
        tiles.map((tile) => tile.id),
      );
    });
  }

  /** Relocate in O(moved tiles), retaining array order and tile identity. */
  relocateAll(entries: { tile: TileInstance; location: Point }[]) {
    const moving = new Set(entries.map((entry) => entry.tile));
    const destinations = new Set<number>();
    for (const { tile, location } of entries) {
      const occupant = this.get(location);
      const key = toIndex(location.x, location.y);
      if (
        this.getRaw(tile.location.x, tile.location.y) !== tile ||
        !isValidTilePosition(location.x, location.y) ||
        (occupant && !moving.has(occupant)) ||
        destinations.has(key)
      )
        throw new Error('Invalid tile move');
      destinations.add(key);
    }
    runInAction(() => {
      for (const { tile } of entries) {
        const { x, y } = tile.location;
        delete this.tileMap[toIndex(x, y)];
        const key = chunkKey(x, y);
        const chunk = this.chunks.get(key);
        chunk?.delete(toIndex(x, y));
        if (chunk?.size === 0) this.chunks.delete(key);
      }
      for (const { tile, location } of entries) {
        tile.globalTilePosition = {
          ...tile.globalTilePosition,
          location: location.clone(),
        };
        const index = toIndex(location.x, location.y);
        this.tileMap[index] = tile;
        const key = chunkKey(location.x, location.y);
        let chunk = this.chunks.get(key);
        if (!chunk) this.chunks.set(key, (chunk = new Map()));
        chunk.set(index, tile);
      }
      if (entries.length) {
        this.structureVersion++;
        this.changes.record(
          this.structureVersion,
          entries.map(({ tile }) => tile.id),
        );
      }
    });
  }

  remove(tile: TileInstance) {
    runInAction(() => {
      const index = toIndex(tile.location.x, tile.location.y);

      if (this.tileMap[index] !== tile) return;
      delete this.tileMap[index];
      if (this.tilesById.get(tile.id) === tile) this.tilesById.delete(tile.id);
      const key = chunkKey(tile.location.x, tile.location.y);
      const chunk = this.chunks.get(key);
      chunk?.delete(index);
      if (chunk?.size === 0) this.chunks.delete(key);
      this.structureVersion++;
      this.changes.record(this.structureVersion, [tile.id]);
      const tileIndex = this.tiles.indexOf(tile);
      if (tileIndex !== -1) {
        this.tiles.splice(tileIndex, 1);
      }
    });
  }

  get(position: IPoint): TileInstance | undefined {
    return this.getRaw(position.x, position.y);
  }

  getById(id: TileId): TileInstance | undefined {
    return this.tilesById.get(id);
  }

  tileChangesSince(version: number): ReadonlySet<TileId> | undefined {
    return this.changes.since(version);
  }

  getRaw(x: number, y: number): TileInstance | undefined {
    if (!isValidTilePosition(x, y)) return undefined;
    return this.tileMap[toIndex(x, y)];
  }

  has(position: IPoint): boolean {
    return this.get(position) !== undefined;
  }

  /** Note this will just check the position and not the equality of the Tile */
  contains(tile: TileInstance): boolean {
    return this.has(Point.fromData(tile.location));
  }

  inRange(bounds: Bounds2D): Map<TileId, TileInstance> {
    // Track membership without depending on every tile or simulation signal.
    void this.structureVersion;
    const result = new Map<TileId, TileInstance>();
    const left = Math.max(MIN_TILE_POSITION_VALUE, Math.ceil(bounds.left));
    const right = Math.min(MAX_TILE_POSITION_VALUE, Math.floor(bounds.right));
    const top = Math.max(MIN_TILE_POSITION_VALUE, Math.ceil(bounds.top));
    const bottom = Math.min(MAX_TILE_POSITION_VALUE, Math.floor(bounds.bottom));
    if (!(left <= right && top <= bottom)) return result;

    const minX = Math.floor(left / CHUNK_SIZE);
    const maxX = Math.floor(right / CHUNK_SIZE);
    const minY = Math.floor(top / CHUNK_SIZE);
    const maxY = Math.floor(bottom / CHUNK_SIZE);
    const collect = (chunk: Map<number, TileInstance>) => {
      for (const tile of chunk.values()) {
        const { x, y } = tile.location;
        if (x >= left && x <= right && y >= top && y <= bottom) result.set(tile.id, tile);
      }
    };

    // Broad selections visit populated chunks; viewports visit only nearby chunks.
    if ((maxX - minX + 1) * (maxY - minY + 1) <= this.chunks.size) {
      for (let x = minX; x <= maxX; x++) {
        for (let y = minY; y <= maxY; y++) {
          const chunk = this.chunks.get(toIndex(x, y));
          if (chunk) collect(chunk);
        }
      }
    } else {
      for (const [key, chunk] of this.chunks) {
        const [x, y] = fromIndex(key);
        if (x >= minX && x <= maxX && y >= minY && y <= maxY) collect(chunk);
      }
    }
    return result;
  }

  getNeighbors(tile: TileInstance): TileInstance[] {
    const neighbors: TileInstance[] = [];
    const { x, y } = tile.location;
    const positions = [
      new Point(x - 1, y),
      new Point(x + 1, y),
      new Point(x, y - 1),
      new Point(x, y + 1),
    ];
    for (const position of positions) {
      const neighbor = this.get(position);
      if (neighbor) {
        neighbors.push(neighbor);
      }
    }
    return neighbors;
  }

  getNeighborsWithDirection(
    tile: TileInstance,
  ): Record<Direction, TileInstance | undefined> {
    return {
      [Direction.LEFT]: this.get(new Point(tile.location.x - 1, tile.location.y)),
      [Direction.RIGHT]: this.get(new Point(tile.location.x + 1, tile.location.y)),
      [Direction.UP]: this.get(new Point(tile.location.x, tile.location.y - 1)),
      [Direction.DOWN]: this.get(new Point(tile.location.x, tile.location.y + 1)),
    };
  }

  /** Function untested, not sure if it works
   * Will also need a new version of this which accounts for if the tiles are actually connected or not
   */
  getDisjointSets(): TileInstance[][] {
    const sets: TileInstance[][] = [];
    const visited = new Set<TileInstance>();
    for (const tile of this.tiles) {
      if (visited.has(tile)) {
        continue;
      }
      const set: TileInstance[] = [];
      const queue: TileInstance[] = [tile];
      while (queue.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const current = queue.shift()!;
        if (visited.has(current)) {
          continue;
        }
        visited.add(current);
        set.push(current);
        for (const neighbor of this.getNeighbors(current)) {
          queue.push(neighbor);
        }
      }
      sets.push(set);
    }
    return sets;
  }

  /**
   * Looks for an interface tile that is not part of a chip and has the given link id
   */
  findInterface(interfaceLinkId: string): TileInstance | undefined {
    // TODO: This could be optimized with a map of link id to tile
    return this.tiles.find(
      (tile) =>
        isInterfaceTile(tile) &&
        tile.state.connectedInterface.interfaceLinkId === interfaceLinkId,
    );
  }

  clone(cloneTiles = true) {
    const manager = TileManagerModel.fromTiles(
      cloneTiles ? this.tiles.map((tile) => tile.clone(this.context)) : this.tiles,
      this.context,
    );

    return manager;
  }

  get dto(): TileManagerDto {
    return {
      tiles: this.tiles.map((tile) => tile.dto),
    };
  }
}
